import { hotjarInit } from './lib/utils/hotjar';
import { handleErrorWithSentry } from '@sentry/sveltekit';
import * as Sentry from '@sentry/sveltekit';
import { getEnvironment } from '$lib/utils/environment';
import { getSentryConfig } from '$lib/services/sentry/config';
import * as DeviceId from '$lib/utils/device-id.js';
import { setGlobalStore } from '$lib/stores/app/store.js';
import { QUERY_PARAM_KEYS } from '$lib/utils/constants.js';
import { setMeta } from '$lib/stores/app/actions.js';
import Cookies from 'js-cookie';
import { AnalyticsService } from '$lib/services/analytics/index.js';
import { qualifyExperiments } from '$lib/services/experiments/index.js';
import { experimentService } from '$lib/services/experiments/client/index.js';
import { ProductExperienceService } from '$lib/services/product-experience/index.js';
import { runCookieTests } from '$lib/utils/cookie-test.js';

Sentry.init({
	...getSentryConfig({
		environment: getEnvironment(),
		// @ts-ignore
		release: __RELEASE_NAME__ || 'local',
		tracesSampleRate: 0.25
	}),
	integrations: [
		Sentry.thirdPartyErrorFilterIntegration({
			filterKeys: ['testing-key'],
			behaviour: 'apply-tag-if-contains-third-party-frames'
		})
	],
	allowUrls: [/https?:\/\/(?:www\.|web\.dev\.)?abcmouse\.com/]
});

export const handleError = handleErrorWithSentry();

hotjarInit();

/**
 * Hook for client init
 */
export async function init() {
	await onAppInit();
}

/**
 * Initializes app
 */
async function onAppInit() {
	const params = new URLSearchParams(window.location.search);
	const linkFromAppPlatform = params.get(QUERY_PARAM_KEYS.LINK_FROM_APP_PLATFORM);

	DeviceId.migrateDeviceIdAndFixErroneousValues();

	const isFirstTimeVisit = !DeviceId.getDeviceId();
	const isDeviceIdInitialized = await DeviceId.initializeDeviceId();

	if (!isDeviceIdInitialized) {
		DeviceId.fireDeviceIdFailureEvent(DeviceId.DEVICE_ID_FAILURE_TYPES.init_failure);
	}

	let deviceId = DeviceId.getDeviceId();

	if (!deviceId) {
		DeviceId.fireDeviceIdFailureEvent(DeviceId.DEVICE_ID_FAILURE_TYPES.get_failure);
		deviceId = 'client_failure:on_app_init:hooks_client.js';
	}

	runCookieTests().catch((err) => {
		Sentry.captureException(err, {
			extra: {
				action: 'Cookie test failed'
			}
		});
	});

	// Initialize meta values (including inApp detection) BEFORE route conditions run
	// Route conditions depend on config.meta.inApp, so this must happen early
	const isLinkFromEmail = params.get(QUERY_PARAM_KEYS.EMAIL_CLICK) ? 'true' : 'false';

	try {
		await setMeta({
			...Cookies.get(),
			acquisitionSource: AnalyticsService.getSourceTag() ?? '',
			deviceId,
			isLinkFromEmail: isLinkFromEmail
		});
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'onAppMount setMeta error'
			}
		});
	}

	await setGlobalStore({
		...(linkFromAppPlatform && { linkFromAppPlatform }),
		isFirstTimeVisit,
		environment: getEnvironment()
	});
	await qualifyExperiments();
	await experimentService.initializeExperiments();

	// Ensure product experience is set from URL before conditions resolve
	await ProductExperienceService.setProductExperienceFromQuery();
}
